<section class="proxy-detail">
  @if (isLoading()) {
  <div class="state-panel" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="56" />
    <h2 class="state-panel__title">Loading request…</h2>
    <p class="state-panel__message">Hang tight while we fetch this proxy request.</p>
  </div>
  } @else if (loadError()) {
  <div class="state-panel" role="alert">
    <span
      class="state-panel__icon"
      [class.state-panel__icon--error]="!notFound()"
      aria-hidden="true"
    >
      <app-icon
        [name]="errorIcon()"
        size="custom"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </span>
    <h1 class="state-panel__title">{{ errorTitle() }}</h1>
    <p class="state-panel__message">{{ loadError() }}</p>
    <a class="primary-button state-panel__button fw-600" routerLink="/requests/proxy-verifications">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Proxy Queue</span>
    </a>
  </div>
  } @else if (request(); as request) {
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a class="breadcrumb__link fw-600" routerLink="/" aria-label="Return to the dashboard"
      >Dashboard</a
    >
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <a
      class="breadcrumb__link fw-600"
      routerLink="/requests/proxy-verifications"
      aria-label="Return to the proxy queue"
      >Proxy Queue</a
    >
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">{{ request.patientName }}</span>
  </nav>

  <header class="page-header flex align-start justify-between gap-16">
    <div>
      <div class="page-header__title-row flex align-center flex-wrap gap-12">
        <h1 id="proxy-detail-title" class="page-title fs-22 fw-700 m-0">{{ headerTitle() }}</h1>
        @if (isMinor()) {
        <span class="badge badge--minor">Minor Patient</span>
        } @if (statusBadge(); as statusBadge) {
        <span class="badge" [class]="'badge--' + statusBadge.kind">{{ statusBadge.label }}</span>
        }
      </div>
      <p class="page-subtitle">{{ submittedSummary() }}</p>
    </div>

    <!-- AC9: back returns to the Proxy Queue, not the dashboard. The queue's tab
         selection lives in the root-scoped ProxyVerificationsFilterStateService,
         so it survives this navigation without being passed through the URL. -->
    <a
      class="secondary-button page-header__back fw-600"
      routerLink="/requests/proxy-verifications"
      aria-label="Return to the proxy queue"
    >
      <app-icon
        name="chevron_left"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Proxy Queue</span>
    </a>
  </header>

  @if (isMinor()) {
  <div class="notice notice--minor flex gap-12" role="note">
    <app-icon
      class="notice__icon"
      name="warning"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <div>
      <p class="notice__title">Minor Patient — Email Authorization Not Required</p>
      <p class="notice__body">
        {{ request.patientName }} (DOB: {{ patientDateOfBirth() }}) is under 18. Per policy, email
        authorization is not required for minor patients — this request goes straight to staff
        review for approval or denial.
      </p>
    </div>
  </div>
  }

  <section class="info-card" aria-labelledby="progress-title">
    <div class="info-card__header flex align-center gap-8">
      <h2 id="progress-title" class="info-card__title">Request Progress</h2>
    </div>

    <div class="info-card__body">
      <ol class="progress flex align-start" aria-label="Request progress">
        @for (step of progressSteps(); track step.label) {
        <li
          class="progress__step flex flex-col align-center gap-8"
          [class.progress__step--travelled]="step.state === 'complete'"
          [attr.aria-current]="step.state === 'current' ? 'step' : null"
        >
          <span class="progress__marker" [class]="'progress__marker--' + step.state">
            @if (step.state === 'complete') {
            <app-icon
              name="check_circle"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            } @else if (step.state === 'failed') {
            <app-icon
              name="block"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            } @else if (step.state === 'skipped') {
            <span aria-hidden="true">–</span>
            } @else {
            <span aria-hidden="true">{{ step.position }}</span>
            }
          </span>
          <span class="progress__label">
            <span class="progress__label-text">{{ step.label }}</span>
            @if (step.note) {
            <span class="progress__note">{{ step.note }}</span>
            }
          </span>
          <span class="sr-only">{{ step.state }}</span>
        </li>
        }
      </ol>
    </div>
  </section>

  <div class="detail-columns">
    <section class="info-card" aria-labelledby="patient-title">
      <div class="info-card__header flex align-center gap-8">
        <app-icon
          class="info-card__icon"
          name="assignment_ind"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <h2 id="patient-title" class="info-card__title">Patient Information</h2>
      </div>

      <div class="info-card__body">
        <p class="entity-name flex align-center flex-wrap gap-8">
          <span>{{ request.patientName }}</span>
          @if (isMinor()) {
          <span class="badge badge--minor">Minor</span>
          }
        </p>
        <dl class="field-list flex flex-col gap-10">
          <div class="flex flex-col gap-2">
            <dt>Date of Birth</dt>
            <dd>{{ patientDateOfBirth() }}</dd>
          </div>
          @if (patientIdentifierMedicalRecordNumber()) {
          <div class="flex flex-col gap-2">
            <dt>MRN</dt>
            <dd>{{ patientIdentifierMedicalRecordNumber() }}</dd>
          </div>
          } @if (patientSsnLastFive()) {
          <div class="flex flex-col gap-2">
            <dt>SSN (last 5)</dt>
            <dd class="masked" [attr.aria-label]="'SSN ending in ' + patientSsnLastFive()">
              {{ maskedPatientSsnLastFive() }}
            </dd>
          </div>
          }
          <div class="flex flex-col gap-2">
            <dt>Email Address</dt>
            <dd>{{ patientEmail() }}</dd>
          </div>
          <div class="flex flex-col gap-2">
            <dt>Account Status</dt>
            <dd>{{ patientAccountStatus() }}</dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="info-card" aria-labelledby="proxy-title">
      <div class="info-card__header flex align-center gap-8">
        <app-icon
          class="info-card__icon"
          name="account_circle"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <h2 id="proxy-title" class="info-card__title">Proxy User Information</h2>
      </div>

      <div class="info-card__body">
        <p class="entity-name">{{ request.name }}</p>
        <dl class="field-list flex flex-col gap-10">
          <div class="flex flex-col gap-2">
            <dt>Relationship</dt>
            <dd>{{ request.relationshipToPatient }}</dd>
          </div>
          <div class="flex flex-col gap-2">
            <dt>Phone Number</dt>
            <dd>{{ proxyPhoneNumber() }}</dd>
          </div>
          <div class="flex flex-col gap-2">
            <dt>Email Address</dt>
            <dd>{{ proxyEmail() }}</dd>
          </div>
          <div class="flex flex-col gap-2">
            <dt>Submitted</dt>
            <dd>{{ submittedTimestamp() }}</dd>
          </div>
        </dl>
      </div>
    </section>
  </div>

  <!-- Proxy requests search through Bolt/Patients/ProxySearch, which audits the
       call against the proxy request id. The panel hides its Phone box because
       that body carries no phone criterion. -->
  @if (adjustSearchOpen()) {
  <app-adjust-search
    [firstName]="adjustFirstName()"
    [lastName]="adjustLastName()"
    [dateOfBirth]="request.patientDateOfBirth ?? ''"
    [lastFiveSsn]="request.patientLastFiveSocialSecurityNumber"
    [mrn]="request.medicalRecordNumber"
    [initialCriteria]="savedAdjustCriteria()"
    (cancelled)="onAdjustSearchCancelled($event)"
    (searchRun)="handleSearchRun($event)"
  ></app-adjust-search>
  } @else {
  <section class="info-card" aria-labelledby="search-results-title">
    <div class="info-card__header flex justify-between align-center gap-8">
      <div class="info-card__title-group flex gap-8">
        <app-icon
          class="info-card__icon"
          name="manage_search"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <div>
          <h2 id="search-results-title" class="info-card__title">Patient Record Search Results</h2>
          <p class="info-card__meta fs-12">
            {{ isSearching() ? 'Searching for matching patient records...' : patientSearchSummary()
            }}
          </p>
        </div>
      </div>

      <button
        type="button"
        class="ghost-button fw-600"
        (click)="openAdjustSearch()"
        aria-label="Adjust patient record search"
        [disabled]="isSearching()"
      >
        <app-icon
          name="tune"
          size="xs"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <span>{{ isSearching() ? 'Searching...' : 'Adjust Search' }}</span>
      </button>
    </div>

    <div
      class="info-card__body info-card__body--spaced flex flex-col gap-12"
      [attr.aria-busy]="isSearching()"
    >
      @if (isSearching()) {
      <div class="search-results-loading empty-state" role="status" aria-live="polite">
        <app-progress-spinner [diameter]="40" />
        <h3 class="fs-16">Searching records...</h3>
        <p class="fs-12">Updated patient matches will appear here when the search completes.</p>
      </div>
      } @else if (searchError(); as searchError) {
      <div class="empty-state" role="alert">
        <span class="empty-state__icon empty-state__icon--error" aria-hidden="true">
          <app-icon
            name="warning"
            size="custom"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
        </span>
        <h3 class="empty-state__title">Search unavailable</h3>
        <p class="empty-state__message">{{ searchError }}</p>
      </div>
      } @else if (patientSearchResults().length > 0) {
      <!-- Plain list of toggle buttons, not an ARIA listbox: a listbox promises
           single-tab-stop arrow-key navigation, which these individually
           focusable cards don't provide. aria-pressed carries the selected
           state, so each button must keep its implicit button role —
           role="listitem" goes on the wrapper, never on the control (a role
           there would drop the button semantics *and* aria-pressed, leaving
           colour as the only cue for the selected record). Same shape as
           linking-request-detail. -->
      <div class="search-results-list" role="list" aria-label="Patient record matches">
        @for (result of patientSearchResults(); track result.patientId; let index = $index) {
        <div role="listitem">
          <button
            type="button"
            class="search-result-card flex flex-col gap-8"
            [class.search-result-card--selected]="selectedSearchResultIndex() === index"
            [attr.aria-pressed]="selectedSearchResultIndex() === index"
            [attr.aria-label]="'Select record for ' + result.displayName"
            (click)="selectSearchResult(index)"
          >
            <span class="search-result-card__name fs-14 fw-600">{{ result.displayName }}</span>
            <span class="search-result-card__meta flex flex-wrap fs-12">
              <span><strong>DOB:</strong> {{ getResultDateOfBirth(result) }}</span>
              <span><strong>MRN:</strong> {{ getResultMedicalRecordNumber(result) }}</span>
              <span><strong>PCP:</strong> {{ getResultPrimaryCareProvider(result) }}</span>
              <span><strong>Address:</strong> {{ getResultAddress(result) }}</span>
              <span><strong>Phone:</strong> {{ getResultPhoneNumber(result) }}</span>
            </span>
          </button>
        </div>
        }
      </div>

      <p class="guidance-text fs-12">
        Select a record above to link, or use Adjust Search to broaden the criteria. If no match
        exists, you can request more information from the patient.
      </p>
      } @else {
      <div class="empty-state" role="status" aria-live="polite">
        <span class="empty-state__icon" aria-hidden="true">
          <app-icon
            name="manage_search"
            size="custom"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
        </span>
        <h3 class="empty-state__title">No matching records found</h3>
        <p class="empty-state__message">
          No patient records matched the submitted details. Use <strong>Adjust Search</strong> to
          broaden the criteria and try again.
        </p>
      </div>
      }
    </div>
  </section>
  }

  <section class="info-card" aria-labelledby="authorization-title">
    <div class="info-card__header flex align-center gap-8">
      <app-icon
        class="info-card__icon"
        name="history"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <h2 id="authorization-title" class="info-card__title">Authorization Status</h2>
    </div>

    <div class="info-card__body">
      <ol class="auth-events flex flex-col" aria-label="Authorization status">
        @for (event of authorizationEvents(); track event.id) {
        <li class="auth-events__item flex gap-12">
          <span class="auth-events__dot" [class]="'auth-events__dot--' + event.tone"></span>
          <div class="auth-events__content">
            <p
              class="auth-events__text"
              [class.auth-events__text--pending]="event.tone === 'pending'"
            >
              {{ event.description }}
            </p>
            <p class="auth-events__detail">{{ event.detail }}</p>
          </div>
        </li>
        }
      </ol>
    </div>
  </section>

  <section class="info-card" aria-labelledby="actions-title">
    <div class="info-card__header flex align-center gap-8">
      <app-icon
        class="info-card__icon"
        name="bolt"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <h2 id="actions-title" class="info-card__title">Actions</h2>
    </div>

    <div class="info-card__body flex flex-col gap-12">
      <div class="actions-row flex flex-wrap align-center gap-12">
        <!-- AC5 / AC3: offered only for an adult request still waiting on the
             patient, and never for a minor. It stays disabled because no admin
             API endpoint sends the authorization email yet — enabled on a PHI
             surface it would read as "the email went out" while doing nothing. -->
        <!-- @if (showEmailAuthorizationAction()) {
        <button
          type="button"
          class="secondary-button fw-600"
          disabled
          title="Sending the authorization email is not available yet."
        >
          <app-icon
            name="mail"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span>{{ emailAuthorizationLabel() }}</span>
        </button>
        } -->

        <!-- Opens the confirmation; the grant itself happens in the modal. -->
        <button
          type="button"
          class="primary-button fw-600"
          (click)="openApproveModal()"
          [disabled]="!canApprove()"
          [attr.aria-describedby]="approveBlockedReason() ? 'approve-hint' : null"
        >
          <app-icon
            name="check_circle"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span>Approve Access</span>
        </button>

        <button
          type="button"
          class="deny-button fw-600"
          (click)="openDenyModal()"
          [disabled]="isDecided()"
        >
          <app-icon
            name="block"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span>Deny Request</span>
        </button>
      </div>

      <!-- Approve is gated on an approvable status and on a selected record, so
           name the reason it is unavailable rather than leaving a dead button. -->
      @if (approveBlockedReason(); as approveBlockedReason) {
      <p id="approve-hint" class="guidance-text fs-12">{{ approveBlockedReason }}</p>
      }
    </div>
  </section>

  <section class="info-card" aria-labelledby="activity-title">
    <div class="info-card__header flex align-center gap-8">
      <app-icon
        class="info-card__icon"
        name="history"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <h2 id="activity-title" class="info-card__title">Activity Log</h2>
    </div>

    <div class="info-card__body">
      @if (notes().length === 0) {
      <p class="empty-note">No notes have been added to this request yet.</p>
      } @else {
      <div class="timeline flex flex-col" role="list" aria-label="Request notes">
        @for (note of notes(); track note.id) {
        <div class="timeline__item flex gap-12" role="listitem">
          <div class="timeline__dot timeline__dot--staff">
            <app-icon
              name="sticky_note_2"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </div>
          <div class="timeline__content">
            <p class="timeline__text">Note added by staff.</p>
            <p class="timeline__note-bubble">{{ note.body }}</p>
            <p class="timeline__author">
              <app-icon
                class="timeline__author-icon"
                name="account_circle"
                size="custom"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
              <span>{{ note.authorName }}</span>
              <span class="timeline__author-sep" aria-hidden="true">·</span>
              <span>{{ note.date }}</span>
              <span class="timeline__author-sep" aria-hidden="true">·</span>
              <span>{{ note.time }}</span>
            </p>
          </div>
        </div>
        }
      </div>
      }

      <!-- The mock's "Add a Comment" box needs a proxy-request notes endpoint,
           which does not exist yet (the linking-request equivalent is
           PatientPortalUserRequestNotesService). Restore the textarea and its
           Add Comment button together with that service. -->
    </div>
  </section>

  @if (activeModal() === 'approve-request' && selectedPatient(); as selectedPatient) {
  <div class="modal-backdrop" role="presentation" (click)="closeModal()">
    <!-- `?? 0` is unreachable for the same reason as the deny modal below. -->
    <app-proxy-approve-request-modal
      [requestId]="requestId ?? 0"
      [version]="request.version"
      [patientName]="request.patientName"
      [proxyName]="request.name"
      [relationshipToPatient]="request.relationshipToPatient"
      [patient]="selectedPatient"
      (cancelled)="closeModal()"
      (approved)="onRequestApproved()"
      (click)="$event.stopPropagation()"
    ></app-proxy-approve-request-modal>
  </div>
  } @if (activeModal() === 'deny-request') {
  <div class="modal-backdrop" role="presentation" (click)="closeModal()">
    <!-- `?? 0` is unreachable: this branch only renders once the request loaded,
         which cannot happen with a null requestId (ngOnInit short-circuits). -->
    <app-proxy-deny-request-modal
      [requestId]="requestId ?? 0"
      [version]="request.version"
      [patientName]="request.patientName"
      [proxyName]="request.name"
      (cancelled)="closeModal()"
      (denied)="onRequestDenied()"
      (click)="$event.stopPropagation()"
    ></app-proxy-deny-request-modal>
  </div>
  } }
</section>
